/**
 * Canonical reference-media contract (VM-990).
 *
 * ONE place where per-model reference limits live. Zod schema caps, config/UI pickers, the
 * creative-gen CLI, and the API's fail-closed pre-dispatch validator all READ this registry —
 * none of them redefine limits. A cross-surface drift guard asserts they agree.
 *
 * This module is deliberately a LEAF: it imports nothing but enums, so every surface
 * (schemas, configs, CLI, api services) can depend on it without an import cycle. Field-name
 * translation lives separately in `routes.ts`; capability and provider dialect are not one concern.
 *
 * Invariant: the contract bounds counts / dimensions / duration ONLY. A reference media URL
 * is never identity authorization — being under the cap does not grant reuse rights.
 */

import {
  EByteDanceImageGenerationModelType,
  EKreaImageGenerationModelType,
  ECreatifyVideoGenerationModelType,
  EHeyGenVideoGenerationModelType,
  GoogleImageGenerationModelType,
  GoogleVideoGenerationModelType,
  HappyHorseVideoGenerationModelType,
  KlingAIVideoGenerationModelType,
  MinimaxVideoGenerationModelType,
  OpenAIImageGenerationModelType,
  SeedanceVideoGenerationModelType,
  SkyReelsVideoGenerationModelType,
  WavespeedVideoGenerationModelType,
} from "../../image/enums"
import type { ImageGenerationModelType, VideoGenerationModelType } from "../../image/enums"

/**
 * Generation modes a contract can be keyed by. Mirrors `CANONICAL_MODE_ORDER` in
 * `../configs/videoModes.ts` — duplicated as a type-only union so this module stays a leaf
 * (configs may read the contract; the contract must never read configs).
 */
export type ReferenceGenerationMode = "t2i" | "i2i" | "t2v" | "i2v" | "r2v" | "v2v"

export type ReferenceMediaModelType = ImageGenerationModelType | VideoGenerationModelType

/** Limits for ONE reference media type (image | video | audio) within a mode. */
export interface ReferenceTypeLimit {
  /** Maximum number of references of this type, when the provider documents one. */
  max?: number
  /** Allowed file extensions / mime hints, when the provider documents them. */
  formats?: string[]
  /** Maximum per-file size in bytes, when the provider documents one. */
  maxBytes?: number
  /**
   * Maximum pixel dimensions, compared edge-agnostically:
   * `min(w,h) <= shortEdge && max(w,h) <= longEdge`. Omit when undocumented — never guess.
   */
  maxDims?: { shortEdge: number; longEdge: number }
  /** Maximum duration in seconds for video/audio references, when documented. */
  maxDurationSec?: number
}

/** Reference limits for one generation mode of one model. */
export interface ReferenceModeContract {
  image?: ReferenceTypeLimit
  video?: ReferenceTypeLimit
  audio?: ReferenceTypeLimit
  /** Cap on image + video + audio references combined, when the provider imposes one. */
  combinedMax?: number
  /** Input modes that cannot be combined with reference inputs (e.g. first/last frame). */
  mutuallyExclusiveWith?: "frame"[]
  /**
   * True → the API probes each reference's real dimensions/duration before dispatch and
   * fails closed when it cannot inspect them. False/absent → counts-only fast path, no probe.
   */
  requiresInspection?: boolean
}

export interface ReferenceMediaContract {
  /** Human label used verbatim in user-facing errors, e.g. "Seedance 2.0". */
  label: string
  modes: Partial<Record<ReferenceGenerationMode, ReferenceModeContract>>
}

/**
 * Seedance 2.0 (standard / fast / mini) — all three share one set of limits.
 *
 * Numbers copied verbatim from the shipped Seedance-only enforcement (PR #6719):
 * - counts 9 / 3 / 3: `.max()` on `reference_{image,video,audio}_urls` and the replicate
 *   `reference_{images,videos,audios}` in `models/replicate/schemas/video/seedance.ts`
 * - combined 12: `validateSeedance2ReferenceCount` in the same file
 * - frame vs reference mutual exclusivity: `validateSeedance2MutualExclusivity`
 * - video 834×1112 short/long edge + fail-closed probe:
 *   `apps/vidmax-api/src/service/seedanceReferenceValidation.ts`
 *
 * Image/audio dimension + byte limits are NOT documented by the provider → counts-only.
 */
const SEEDANCE_2_REFERENCE_CONTRACT: ReferenceMediaContract = {
  label: "Seedance 2.0",
  modes: {
    r2v: {
      image: { max: 9 },
      video: { max: 3, maxDims: { shortEdge: 834, longEdge: 1112 } },
      audio: { max: 3 },
      combinedMax: 12,
      mutuallyExclusiveWith: ["frame"],
      requiresInspection: true,
    },
  },
}

/**
 * Seedance 2.5 (fal + replicate).
 *
 * Every number below is declared by fal's own `bytedance/seedance-2.5/reference-to-video`
 * schema, fetched 2026-08-11. Quoted so a reviewer can check each one:
 * - images: "Max 30 MB per image. Up to 30 images." → max 30, maxBytes 30MB
 * - videos: "Up to 10 videos. Each video must be 1.8 to 30.2 seconds and no larger than 200 MB;
 *   combined duration must not exceed 30.2 seconds. Dimensions must be 300 to 6,000 pixels per
 *   side, aspect ratio 0.4 to 2.5, and frame rate 24 to 60 FPS."
 * - audio: "Up to 10 files. Each file must be 1.8 to 30.2 seconds and no larger than 15 MB;
 *   combined duration must not exceed 30.2 seconds."
 * - "Total files across all modalities must not exceed 50." → combinedMax 50
 *
 * `combinedMax` is deliberately OMITTED: the per-type maxima sum to exactly 50, so the provider's
 * "total ≤ 50" is already implied and never independently binding. Declaring it anyway fails the
 * VM-990 drift guard, which requires a combined cap to be expressible as an over-limit payload
 * (it cannot be, at 30+10+10=50). If a per-type cap is ever raised, add `combinedMax: 50` back —
 * at that point it starts binding. Aspect-ratio and FPS bounds have no contract field; they stay
 * provider-side.
 * NOTE the counts are far larger than 2.0's 9/3/3 — do NOT copy the 2.0 contract for 2.5.
 */
const SEEDANCE_2_5_REFERENCE_CONTRACT: ReferenceMediaContract = {
  label: "Seedance 2.5",
  modes: {
    r2v: {
      image: { max: 30, maxBytes: 30 * 1024 * 1024 },
      video: {
        max: 10,
        maxBytes: 200 * 1024 * 1024,
        maxDims: { shortEdge: 6000, longEdge: 6000 },
        maxDurationSec: 30.2,
      },
      audio: { max: 10, maxBytes: 15 * 1024 * 1024, maxDurationSec: 30.2 },
      mutuallyExclusiveWith: ["frame"],
      requiresInspection: true,
    },
  },
}

/**
 * MiniMax H3 reference-to-video (FAL-only).
 *
 * Every number is declared by fal's own OpenAPI for `minimax/h3/reference-to-video`, fetched
 * 2026-07-31 from https://fal.ai/api/openapi/queue/openapi.json?endpoint_id=minimax/h3/reference-to-video
 * (the model page itself is bot-walled, so the OpenAPI doc is the citable source):
 * - `maxItems` 9 / 3 / 3 on `reference_{image,video,audio}_urls`
 * - "Reference images, videos, and audio clips must add up to at most 12 files." → combinedMax.
 *   It genuinely binds: the per-type maxima sum to 15.
 * - "(2-15 seconds each, combined duration at most 15 seconds)" on both video and audio
 *   → maxDurationSec 15, which is what earns `requiresInspection` (the probe enforces it).
 *
 * No dimension or byte cap is documented → none declared.
 *
 * Frame/reference exclusivity IS declared. The frame inputs (image_url / end_image_url) belong to
 * the i2v endpoint and the references to r2v; because mode resolution routes ANY request carrying
 * references to r2v, a combined request would reach an endpoint with no frame field and lose the
 * frames silently. Declaring it here fails the request pre-dispatch instead.
 *
 * Two documented limits are NOT expressible in this shape and are enforced by the provider:
 * the 2s per-clip MINIMUM and the 15s COMBINED duration across reference video/audio. The
 * contract carries only per-file maxima, so those are deliberately left to fal rather than
 * approximated here — an invented cap would reject valid media.
 *
 * "Audio cannot be the only reference input" is likewise a cross-modality rule this shape cannot
 * express; it lives in `checkMinimaxH3CrossFieldRules` (schemas/video/minimax.ts).
 */
const MINIMAX_H3_REFERENCE_CONTRACT: ReferenceMediaContract = {
  label: "MiniMax H3",
  modes: {
    r2v: {
      image: { max: 9 },
      video: { max: 3, maxDurationSec: 15 },
      audio: { max: 3, maxDurationSec: 15 },
      combinedMax: 12,
      mutuallyExclusiveWith: ["frame"],
      requiresInspection: true,
    },
  },
}

/**
 * Counts-only entry helper for the models backfilled in VM-990 chunk 4.
 *
 * Every `max` below is copied from that model's shipped zod schema — the repo's recorded
 * encoding of the provider limit — and the drift guard asserts the two stay equal. NO provider
 * documents a dimension, byte, or duration cap for these, so none is declared: an invented
 * number would reject valid user media. Consequently none sets `requiresInspection`, so they
 * keep the no-probe fast path.
 *
 * Frame-vs-reference exclusivity is likewise NOT declared for any of them — no provider doc
 * states it, and asserting it would newly reject requests that work today.
 */
const countsOnly = (
  label: string,
  limits: { image?: number; video?: number; audio?: number },
): ReferenceMediaContract => ({
  label,
  modes: {
    r2v: {
      ...(limits.image !== undefined ? { image: { max: limits.image } } : {}),
      ...(limits.video !== undefined ? { video: { max: limits.video } } : {}),
      ...(limits.audio !== undefined ? { audio: { max: limits.audio } } : {}),
    },
  },
})

// Replicate documents one JPG/PNG/BMP/WebP first frame up to 10 MB for Wan 3 I2V.
// FAL publishes no tighter model-specific cap, so the shared contract records only this bound.
const WAN_3_I2V_REFERENCE_CONTRACT: ReferenceMediaContract = {
  label: "Wan 3.0 Image-to-Video",
  modes: {
    i2v: {
      image: { max: 1, formats: ["jpg", "png", "bmp", "webp"], maxBytes: 10_000_000 },
      requiresInspection: true,
    },
  },
}

/** Image-reference support; an omitted max means the schema/provider publishes no cap. */
export const KREA_3_MAX_REFERENCES = 8

const imageReferences = (label: string, max?: number): ReferenceMediaContract => ({
  label,
  modes: { i2i: { image: max === undefined ? {} : { max } } },
})

/**
 * Registry of every model with reference-media inputs. Absence = the model takes no
 * references, and the validator must no-op for it (no probe, no throw).
 */
export const REFERENCE_MEDIA_CONTRACTS: Partial<Record<ReferenceMediaModelType, ReferenceMediaContract>> = {
  [EKreaImageGenerationModelType.KREA_3_EDIT]: imageReferences("Krea 3 Edit", KREA_3_MAX_REFERENCES),
  [OpenAIImageGenerationModelType.GPT_IMAGE_2_EDIT]: imageReferences("GPT Image 2 Edit", 16),
  [ECreatifyVideoGenerationModelType.BOREAL]: {
    label: "Creatify Boreal",
    modes: {
      t2v: { image: { max: 1 }, audio: { max: 1 } },
    },
  },
  // Image generation (VM-1016). These are the image configs that expose the generic
  // creative-gen `--reference` path. Their canonical field is preserved through zod and then
  // translated by the provider adapter; the contract keeps that path from silently dropping.
  [GoogleImageGenerationModelType.NANO_BANANA_PRO]: imageReferences("Google Nano Banana Pro", 14),
  [GoogleImageGenerationModelType.NANO_BANANA_2_1]: {
    label: "Google Nano Banana 2.1",
    modes: {
      i2i: {
        image: { max: 10, formats: ["jpeg", "jpg", "png", "webp"], maxBytes: 30_000_000 },
        requiresInspection: true,
      },
    },
  },
  [GoogleImageGenerationModelType.NANO_BANANA_2]: imageReferences("Google Nano Banana 2", 14),
  // Replicate publishes no count cap in the registered schema. Keep support explicit without
  // inventing a limit; the drift guard requires the schema to remain unbounded too.
  [GoogleImageGenerationModelType.GEMINI_2_5_FLASH_IMAGE]: imageReferences("Google Gemini 2.5 Flash Image"),
  [EByteDanceImageGenerationModelType.SEEDREAM_4_5]: imageReferences("ByteDance Seedream 4.5", 14),

  [SeedanceVideoGenerationModelType.SEEDANCE_2]: SEEDANCE_2_REFERENCE_CONTRACT,
  [SeedanceVideoGenerationModelType.SEEDANCE_2_FAST]: SEEDANCE_2_REFERENCE_CONTRACT,
  [SeedanceVideoGenerationModelType.SEEDANCE_2_MINI]: SEEDANCE_2_REFERENCE_CONTRACT,
  [SeedanceVideoGenerationModelType.SEEDANCE_2_5]: SEEDANCE_2_5_REFERENCE_CONTRACT,

  [MinimaxVideoGenerationModelType.H3]: MINIMAX_H3_REFERENCE_CONTRACT,
  [EHeyGenVideoGenerationModelType.HEYGEN_VIDEO]: {
    label: "HeyGen Video",
    modes: { r2v: { image: { max: 9 }, mutuallyExclusiveWith: ["frame"] } },
  },

  // Google — per-variant caps, deliberately different.
  // veo-3.1: `reference_images: z.array(z.string()).min(1).max(3)` — schemas/video/google.ts:40
  [GoogleVideoGenerationModelType.VEO_3_1]: countsOnly("Google Veo 3.1", { image: 3 }),
  // gemini-omni: `reference_image_urls: z.array(z.string().url()).min(1).max(10)` — google.ts:79
  [GoogleVideoGenerationModelType.GEMINI_OMNI_FLASH_PREVIEW]: countsOnly("Gemini Omni Flash", { image: 10 }),
  [GoogleVideoGenerationModelType.GEMINI_OMNI_FLASH_1_1]: {
    label: "Gemini Omni Flash 1.1",
    modes: {
      r2v: {
        // Shared product cap follows WaveSpeed's documented max of 10
        // (https://wavespeed.ai/docs/docs-api/google/google-gemini-omni-1.1-flash-reference-to-video); FAL publishes no image max.
        image: { max: 10 },
        video: { max: 3, maxDurationSec: 3 },
        mutuallyExclusiveWith: ["frame"],
        requiresInspection: true,
      },
    },
  },

  // Kling 1.6 — `reference_images: z.array(z.string()).max(4)` — schemas/video/kling.ts:49 (standard), :60 (pro)
  [KlingAIVideoGenerationModelType.KLING_STANDARD_1_6]: countsOnly("Kling 1.6 Standard", { image: 4 }),
  [KlingAIVideoGenerationModelType.KLING_PRO_1_6]: countsOnly("Kling 1.6 Pro", { image: 4 }),

  // Kling motion control — one driving video: `video_urls: z.array(z.string().url()).min(1).max(1)`
  // — schemas/video/kling.ts:143 (3.0), :152 (2.6). Character images route to `input_urls`:
  // `z.array(z.string().url()).min(1).max(4)` — schemas/video/kling.ts:142 (3.0), :151 (2.6).
  [KlingAIVideoGenerationModelType.KLING_3_0_MOTION_CONTROL]: countsOnly("Kling 3.0 Motion Control", {
    image: 4,
    video: 1,
  }),
  [KlingAIVideoGenerationModelType.KLING_2_6_MOTION_CONTROL]: countsOnly("Kling 2.6 Motion Control", {
    image: 4,
    video: 1,
  }),

  // Single-reference models — the field is a bare string, so exactly one reference.
  // https://docs.kie.ai/market/happyhorse/reference-to-video.md — schemas/video/happyhorse.ts:36
  [HappyHorseVideoGenerationModelType.HAPPYHORSE_R2V]: countsOnly("HappyHorse Reference-to-Video", { image: 1 }),
  // `reference_image: z.string().min(1)` (required) — schemas/video/skyreels.ts:24
  [SkyReelsVideoGenerationModelType.SKYREELS_V4_R2V]: countsOnly("SkyReels V4 Reference-to-Video", { image: 1 }),
  // https://docs.kie.ai/market/wan/2-7-r2v.md — `reference_image` + `reference_video`, both
  // single optional strings — schemas/video/wavespeed.ts:177-178
  [WavespeedVideoGenerationModelType.WAN_2_7_R2V]: countsOnly("Wan 2.7 Reference-to-Video", { image: 1, video: 1 }),
  // https://docs.kie.ai/market/wan/2-7-videoedit.md — schemas/video/wavespeed.ts:200
  [WavespeedVideoGenerationModelType.WAN_2_7_VIDEOEDIT]: countsOnly("Wan 2.7 VideoEdit", { image: 1 }),
  [WavespeedVideoGenerationModelType.WAN_3_0_I2V]: WAN_3_I2V_REFERENCE_CONTRACT,
  [WavespeedVideoGenerationModelType.WAN_3_0_PRIME_I2V]: {
    ...WAN_3_I2V_REFERENCE_CONTRACT,
    label: "Wan 3.0 Prime Image-to-Video",
  },
}

/** `undefined` = this model has no reference inputs. */
export const getReferenceMediaContract = (modelId: string): ReferenceMediaContract | undefined =>
  REFERENCE_MEDIA_CONTRACTS[modelId as ReferenceMediaModelType]

/** Every model id that declares reference inputs — used by the cross-surface drift guard. */
export const getReferenceModelIds = (): ReferenceMediaModelType[] =>
  Object.keys(REFERENCE_MEDIA_CONTRACTS) as ReferenceMediaModelType[]
